import { useStyletron, withStyle } from "baseui";
import { getStripeId } from "../../services/resourceGroups/utils";
import {
  useGetStripeSubscriptionByIdQuery,
  useGetSubscriptionUsageQuery,
} from "../../services/resourceGroups/subscriptionApi";
import { StyledTable, StyledHeadCell, StyledBodyCell } from "baseui/table-grid";
import { Tag } from "baseui/tag";
import { ISubscription } from "@oqdo/automationapps/Memberships/Subscription";
import { LoadingView } from "../../components/LoadingView";
import { HeadingXSmall, LabelMedium, ParagraphXSmall } from "baseui/typography";
import { StyledDivider } from "baseui/divider";
import { Table } from "baseui/table-semantic";
import { FormattedMessage } from "react-intl";
import { StyledUsagePerBuildingRoot } from "./styled-components";
import { AccessRoles, useHasRole } from "../../hooks/useHasRole";
import { getMergedProducts } from "../subscriptions/utils";
import HierarchyObjectNavigation from "../../components/HierarchyNavigation/HierarchyObjectNavigation";
import { IAutomationAppBase } from "@oqdo/automationapps/Abstractions/AutomationAppBase";

const SubscriptionUsage = (props: { subscription: ISubscription }) => {
  const [css, theme] = useStyletron();
  const { subscription } = props;
  const stripeId = getStripeId(subscription);
  const products = subscription?.products;
  const { data: stripeSubscription } = useGetStripeSubscriptionByIdQuery(stripeId, { skip: !stripeId });
  const isSa = useHasRole(AccessRoles.SuperAdmin);

  const mergedProducts = getMergedProducts(products, stripeSubscription?.products);

  const tabelcolumns = isSa ? "minmax(400px, max-content) auto auto" : "minmax(400px, max-content) auto";
  return (
    <>
      <StyledTable role="grid" $gridTemplateColumns={tabelcolumns}>
        <div role="row" className={css({ display: "contents" })}>
          <StyledHeadCell>
            <FormattedMessage id="product" defaultMessage={"Product"} />
          </StyledHeadCell>
          <StyledHeadCell>
            <FormattedMessage id="quantityUsed" defaultMessage={"Quantity Used"} />
          </StyledHeadCell>
          {isSa && (
            <StyledHeadCell>
              <FormattedMessage id="quantityPaid" defaultMessage={"Quantity Paid"} />
            </StyledHeadCell>
          )}
        </div>
        {mergedProducts.map(([key, value]) => (
          <Row showStripeValue={isSa} key={key} item={[key, value.val1]} stripeValue={value.val2} />
        ))}
      </StyledTable>

      <HeadingXSmall>
        <FormattedMessage id="detailsPerBuilding" defaultMessage={"Details per Building"} />
      </HeadingXSmall>
      <UsageDetails subscriptionId={subscription.id} />
    </>
  );
};

export default SubscriptionUsage;

const Row: React.FC<{
  item: [string, number];
  stripeValue?: number;
  showStripeValue: boolean;
}> = (props) => {
  const { item, stripeValue, showStripeValue } = props;
  const [css, theme] = useStyletron();

  const itemKey = item[0];
  const itemValue = item[1] ?? 0;
  const paidValue = stripeValue ?? 0;

  if (itemValue === 0 && paidValue === 0) return <></>;

  return (
    <div role="row" key={item[0]} className={css({ display: "contents" })}>
      <StyledBodyCell>{itemKey.replaceAll("_", ".")}</StyledBodyCell>
      <CenteredBodyCell>
        <Tag closeable={false} kind="neutral">
          {itemValue}
        </Tag>
      </CenteredBodyCell>
      {showStripeValue && (
        <CenteredBodyCell>
          <Tag closeable={false} kind={paidTagColor(itemValue, paidValue)}>
            {paidValue}
          </Tag>
        </CenteredBodyCell>
      )}
    </div>
  );
};

function paidTagColor(used: number, paid: number) {
  if (used && !paid) return "negative";
  if (used > paid) return "negative";
  if (used === paid) return "positive";
  if (used < paid) return "accent";
  return "neutral";
}

const CenteredBodyCell = withStyle(StyledBodyCell, {
  display: "flex",
  alignItems: "center",
});

const UsageDetails: React.FC<{ subscriptionId: string }> = ({ subscriptionId }) => {
  const [css, theme] = useStyletron();
  const { data, isLoading, error, isSuccess } = useGetSubscriptionUsageQuery(subscriptionId);

  // Collect all unique product names
  const productSet = new Set<string>();
  data?.forEach((v) => v.products.forEach((p) => productSet.add(p.name)));
  const productNames = Array.from(productSet);

  // Prepare table columns: Building + product columns
  const columns = [
    <FormattedMessage id="building" defaultMessage={"Building"} />,
    ...productNames.map((name) => <FormattedMessage id={name} defaultMessage={name} />),
  ];

  // Prepare table data: each row is [building, ...product quantities]
  const tableData =
    data?.map((v) => {
      const productMap = new Map(v.products.map((p) => [p.name, p.quantity > 0 ? p.quantity : undefined]));
      return {
        building: v.building as IAutomationAppBase,
        products: productNames.map((name) => productMap.get(name) ?? undefined),
        isSummary: false,
      };
    }) ?? [];

  // Calculate summary row
  const summaryRow = {
    building: undefined,
    products: productNames.map((name) => {
      let sum = 0;
      data?.forEach((v) => {
        const prod = v.products.find((p) => p.name === name);
        if (prod && prod.quantity > 0) sum += prod.quantity;
      });
      return sum > 0 ? sum : undefined;
    }),
    isSummary: true,
  };

  // Add summary row if there is data
  const tableDataWithSummary = tableData.length > 0 ? [...tableData, summaryRow] : tableData;

  // Row component using StyledBodyCell
  const UsageRow = ({
    building,
    products,
  }: {
    building: IAutomationAppBase | undefined;
    products: (string | number | React.ReactNode | undefined)[];
  }) => (
    <div role="row" className={css({ display: "contents" })}>
      <StyledBodyCell>
        {building ? (
          <HierarchyObjectNavigation {...building}></HierarchyObjectNavigation>
        ) : (
          <FormattedMessage id="summary" defaultMessage={"Summary"} />
        )}
      </StyledBodyCell>
      {products.map((val, idx) => (
        <CenteredBodyCell key={idx}>
          {val ? (
            <Tag closeable={false} kind="neutral">
              {val}
            </Tag>
          ) : (
            "-"
          )}
        </CenteredBodyCell>
      ))}
    </div>
  );

  const SummaryRow = ({ products }: { products: (string | number | React.ReactNode | undefined)[] }) => (
    <div role="row" className={css({ display: "contents", fontWeight: 700 })}>
      <StyledHeadCell>
        <FormattedMessage id="summary" defaultMessage={"Summary"} />
      </StyledHeadCell>
      {products.map((val, idx) => (
        <StyledHeadCell key={idx}>
          {val ? (
            <Tag closeable={false} kind="neutral">
              {val}
            </Tag>
          ) : (
            "-"
          )}
        </StyledHeadCell>
      ))}
    </div>
  );

  return (
    <LoadingView isLoading={isLoading} error={error} isSucess={isSuccess}>
      <StyledUsagePerBuildingRoot>
        {tableData.length === 0 ? (
          <ParagraphXSmall color={theme.colors.contentTertiary}>
            <FormattedMessage id="noUsedProducts" defaultMessage={"No used products"} />
          </ParagraphXSmall>
        ) : (
          <StyledTable
            role="grid"
            $gridTemplateColumns={`minmax(200px, max-content) repeat(${productNames.length}, auto)`}>
            <div role="row" className={css({ display: "contents", fontWeight: 600 })}>
              <StyledHeadCell>
                <FormattedMessage id="building" defaultMessage={"Building"} />
              </StyledHeadCell>
              {productNames.map((name) => (
                <StyledHeadCell key={name}>
                  <FormattedMessage id={name} defaultMessage={name} />
                </StyledHeadCell>
              ))}
            </div>
            {tableDataWithSummary.map((row, idx) => (
              <>
                {row.isSummary ? (
                  <SummaryRow key={idx} products={row.products} />
                ) : (
                  <UsageRow key={idx} building={row.building} products={row.products} />
                )}
              </>
            ))}
          </StyledTable>
        )}
      </StyledUsagePerBuildingRoot>
    </LoadingView>
  );
};
